import React, { useEffect, useState } from 'react';
import {
  createCoinCoupon,
  getCoinCoupons,
  updateCoinCoupon,
} from '../api/skipzyAdmin';

const EMPTY_FORM = {
  code: '',
  coins: '',
  expiry_days: '',
  valid_until: '',
  max_redemptions: '',
  notes: '',
};

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

function formatDate(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function CoinCouponsSection() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      setCoupons(await getCoinCoupons());
    } catch (err) {
      setError(errorMessage(err, 'Failed to load coin coupons'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function addCoupon(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const code = form.code.trim().toUpperCase();
    const coins = parseInt(form.coins, 10);
    const expiryDays = parseInt(form.expiry_days, 10);
    if (!code || !(coins > 0) || !(expiryDays > 0)) {
      setError('Code, coins and coin expiry (days) are required.');
      return;
    }
    const payload = {
      code,
      coins,
      expiry_days: expiryDays,
      notes: form.notes.trim() || null,
      // datetime-local has no zone; it's the operator's local time.
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
    };
    setCreating(true);
    try {
      await createCoinCoupon(payload);
      setSuccess(`Coin coupon ${code} created.`);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Failed to create coin coupon'));
    } finally {
      setCreating(false);
    }
  }

  async function toggleCoupon(coupon) {
    const next = !coupon.is_active;
    if (!next && !window.confirm(`Deactivate ${coupon.code}? Customers will no longer be able to redeem it.`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    setBusyKey(`coin:${coupon.code}`);
    try {
      await updateCoinCoupon(coupon.code, { is_active: next });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Failed to update coin coupon'));
    } finally {
      setBusyKey(null);
    }
  }

  const input = 'w-full border border-gray-200 rounded px-3 py-2 text-sm';

  return (
    <section className="mb-8">
      <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Coin Coupons</h3>
      <p className="text-sm text-gray-500 mb-4">
        Codes customers type into My Wallet in the app to get coins instantly. Each customer can use a code once.
      </p>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {success && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{success}</div>}

      <div className="mb-6">
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Create coin coupon</h4>
        <form onSubmit={addCoupon} className="bg-white border border-gray-200 rounded p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Code</label>
            <input
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="e.g. WELCOME100"
              maxLength={32}
              className={`${input} font-mono`}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Coins to credit</label>
            <input
              type="number"
              min="1"
              value={form.coins}
              onChange={e => setForm(f => ({ ...f, coins: e.target.value }))}
              className={input}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Coins expire after (days)</label>
            <input
              type="number"
              min="1"
              value={form.expiry_days}
              onChange={e => setForm(f => ({ ...f, expiry_days: e.target.value }))}
              className={input}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Code valid until (optional)</label>
            <input
              type="datetime-local"
              value={form.valid_until}
              onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
              className={input}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Max total redemptions (optional)</label>
            <input
              type="number"
              min="1"
              value={form.max_redemptions}
              onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
              placeholder="Unlimited"
              className={input}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
            <input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              maxLength={500}
              className={input}
            />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button
              type="submit"
              disabled={creating}
              className="text-sm font-semibold bg-zoddle-green text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create coupon'}
            </button>
          </div>
        </form>
      </div>

      <div>
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">All coin coupons</h4>
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Coins</th>
                <th className="text-left px-4 py-2">Coins expire</th>
                <th className="text-left px-4 py-2">Code valid until</th>
                <th className="text-left px-4 py-2">Redeemed</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-3 text-gray-400 text-sm">Loading…</td></tr>
              )}
              {!loading && coupons.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-3 text-gray-400 text-sm">No coin coupons yet.</td></tr>
              )}
              {coupons.map(c => (
                <tr key={c.code} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-mono text-gray-800">
                    {c.code}
                    {c.notes && <div className="font-sans text-xs text-gray-400">{c.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{c.coins}</td>
                  <td className="px-4 py-3 text-gray-600">{c.expiry_days} days</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(c.valid_until)}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.redeemed_count}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {c.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleCoupon(c)}
                      disabled={busyKey === `coin:${c.code}`}
                      className={`text-sm font-semibold px-3 py-1.5 rounded disabled:opacity-50 ${
                        c.is_active
                          ? 'border border-zoddle-pink text-zoddle-pink hover:bg-red-50'
                          : 'bg-zoddle-green text-white hover:opacity-90'
                      }`}
                    >
                      {c.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
